import Image from "@components/Image";
import { t } from "i18next";
import { Input } from "antd";
import { IconDelete, IconFolder, IconInputDel } from "@icons/index";
import TruncateTitle from "@components/TruncateTitle";
import classNames from "classnames";
import "./index.scss";
import { useAppDispatch } from "@hooks/redux";
import { fetchVideoLinkPreview, updateVideo } from "@pages/aiStudio/ScriptRewrite/scriptRewriteSlice";
import { useEffect, useRef, useState } from "react";
import { Section } from "@src/ui/Section";
import { Trans } from "react-i18next";
import { ensureHttps, stripProtocol } from "@pages/aiStudio/ScriptExport/insight";
import { useDebounce } from "@hooks/utils";

interface VideoReferenceProps {
  videoId?: string;
  videoDetail?: any;
  status?: string;
  disabled?: boolean;
  isOpen?: boolean;
  className?: string;
  handleShowModal?: (open: boolean) => void;
}

const VideoReference = (props: VideoReferenceProps) => {
  const { videoId, videoDetail, status, disabled, isOpen, handleShowModal } = props;
  const dispatch = useAppDispatch();
  const [inputWorld, setInputWorld] = useState<string>("");

  const handleSearch = async () => {
    if (!inputWorld.trim()) return;
    dispatch(fetchVideoLinkPreview({ videoLink: ensureHttps(inputWorld) }));
  };

  const handleSearchRef = useRef<() => void>(() => {});
  handleSearchRef.current = handleSearch;
  const debouncedSearch = useDebounce(() => handleSearchRef.current(), 500);

  useEffect(() => {
    if (videoDetail?.status === "succeeded") {
      setInputWorld("");
    }
  }, [videoDetail]);

  return (
    <section className="mb-[16px] relative VideoReference">
      <div className="flex items-center justify-between mb-[8px]">
        <div className="gap-[4px] font-semibold text-[16px]">
          <Trans>Reference Video</Trans>
        </div>
        {!(disabled && status !== "idle") && (
          <div
            className="flex items-center gap-[4px] text-kds-fg-soft cursor-pointer"
            onClick={() => {
              handleShowModal && handleShowModal(true);
            }}
          >
            <IconFolder />
            <Trans>Add from library</Trans>
          </div>
        )}
      </div>

      {videoId ? (
        <Section
          status={videoDetail.status}
          error={videoDetail.error}
          className="bg-kds-bg-card border border-kds-border-subtle rounded-[8px] h-[88px] mb-[16px]"
        >
          <div className="flex items-center gap-[8px] p-[8px]">
            <div className="w-[54px] h-[72px] rounded-[4px] border-0 overflow-hidden">
              <Image
                className="w-[54px] h-[72px] border-none!"
                url={videoDetail?.data?.coverImageUrl}
                hoverEnlarge={true}
              ></Image>
            </div>
            <TruncateTitle
              text={videoDetail?.data?.description || "-"}
              lineclamp2
              placement="top"
              className="flex-1"
            ></TruncateTitle>
            {!disabled && (
              <div
                className={classNames(
                  "flex flex-nowrap items-center gap-[2px] text-kds-accent icon-del transition-all cursor-pointer",
                  isOpen && "icon-del-visible",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(
                    updateVideo({
                      videoId: "",
                      videoDetail: null,
                      videoSource: "",
                    }),
                  );
                }}
              >
                <IconDelete className="w-[16px] h-[16px]" />
              </div>
            )}
          </div>
        </Section>
      ) : (
        <>
          <Input
            value={inputWorld}
            placeholder={t("Paste a TikTok, Kalodata, or YouTube Shorts video link.")}
            className="max-w-[800px] h-[56px]"
            size="large"
            disabled={videoDetail.status === "loading" || status === "loading"}
            allowClear={{ clearIcon: <IconInputDel className="w-[16px] h-[16px]" /> }}
            prefix={
              <div className="font-semibold text-[14px] text-kds-fg flex items-center pl-[4px]">
                <span>Https://</span>
                <div className="w-px h-[40px] bg-kds-border-solid ml-[8px] mr-[4px]"></div>
              </div>
            }
            onKeyDown={(e) => {
              if (videoDetail.status === "loading" || status === "loading") return;
              if ((e.code === "Enter" || e.code === "NumpadEnter") && e.keyCode !== 229) {
                debouncedSearch();
              }
            }}
            onChange={(e) => {
              const stripped = stripProtocol(e.target.value);
              setInputWorld(stripped);
              if (stripped.trim()) {
                debouncedSearch();
              }
            }}
          />
          {videoDetail.status === "failed" && (
            <div className="text-kds-danger mt-[8px] text-[12px]">
              * {t(videoDetail.error || "Video link not found, Please re-enter it.")}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default VideoReference;
